<template>
    <BasePanel
        headline='lang_GENERIC_AZ_PANEL_CAPTION_METADATA'
        :alvqa="alvqa"
    >
        <template v-slot:panelContent>
            <v-row class="pt-4">
                <v-col cols="3">
                    <v-row>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="Language"
                            :items="languageItems" 
                            :label="$t('lang_AZ_PERSONS_CAPTION_LANGUAGE')" 
                            :selection="true"
                            :clearable="false"
                            :alvqa="alvqa + '-dropdown-language'"
                        />
                    </v-row>
                    <v-row>
                        <BaseEditPageDatePicker 
                            :context="context"
                            field="Birthday"
                            :label="$t('lang_GENERIC_AZ_CAPTION_BIRTHDAY')"
                            :alvqa="alvqa + '-datepicker-birthday'"
                        />
                    </v-row>

                    <!-- Meisel: Schutzfrist -->
                    <v-row>
                        <BaseEditPageDropdown
                            :label="$t('lang_GENERIC_AZ_CAPTION_YEAR_OF_DEATH')"
                            :items="yearItems"
                            :context="context"
                            field="YearOfDeath"
                            :clearable="true"
                            :alvqa="alvqa + '-list-dropdown-year-of-death'"
                        />
                    </v-row>
                    <v-row>
                        <BaseEditPageTextField 
                            :context="context"
                            field="CustomerNumber"
                            :label="$t('lang_GENERIC_AZ_CAPTION_CUSTOMER_NUMBER')"
                            :alvqa="alvqa + '-textfield-customer-number'"
                        />
                    </v-row>
                    <!-- if used with a HelpIcon, use the below style and removeRightMargin -->
                    <v-row style="flex-wrap: nowrap;">
                        <BaseEditPageDropdown 
                            :context="context"
                            field="Currency"
                            :items="dropdownCurrency" 
                            :clearable="true"
                            :label="$t('lang_GENERIC_CAPTION_CURRENCY')"
                            :alvqa="alvqa + '-dropdown-currency'"
                            :removeRightMargin="true"
                        /> 
                        <!-- if used next to a dropdown, use the below class for padding/margins -->
                        <HelpIcon
                            class="ml-0 mt-3"
                            :urls="{
                                de: '196411417',
                                en: '199262212',
                                ko: '201097220',
                            }"
                            :tooltipText="$t('lang_GENERIC_CLICK_TO_HELP_CENTER')"
                        />
                    </v-row>
                </v-col>

                <v-col cols="4">
                    <v-row>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="LabelCompany"
                            :items="labelCompanyItems" 
                            :label="$t('lang_GENERIC_AZ_CAPTION_ASSIGN_TO_LABEL_COMPANY')" 
                            :selection="true"
                            itemText="name"
                            itemValue="alvId"
                            :alvqa="alvqa + '-dropdown-label-company'"
                        />
                    </v-row>
                    <v-row>
                        <BaseEditPageTextField 
                            :context="context"
                            field="GemaNextPrefix"
                            :label="$t('lang_AZ_PERSONS_CAPTION_GEMA_NEXT_PREFIX')"
                            :alvqa="alvqa + '-textfield-gema-next-prefix'"
                        /> 
                    </v-row>
                    <v-row>
                            <BaseEditPageTextField 
                            :context="context"
                            field="GemaNextSuffix"
                            :label="$t('lang_AZ_PERSONS_CAPTION_NEXT_VALUE')"
                            :alvqa="alvqa + '-textfield-gema-next-suffix'"
                        />
                    </v-row>
                </v-col>

                <v-col cols="5"> 
                    <v-row>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="PublishingCompany"
                            :items="publishingCompanyItems" 
                            :label="$t('lang_GENERIC_AZ_CAPTION_ASSIGN_TO_PUBLISHING_COMPANY')" 
                            :selection="true"
                            itemText="name"
                            itemValue="alvId"
                            :alvqa="alvqa + '-dropdown-publishing-company'"
                        />
                    </v-row>
                    <v-row>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="GemaMember"
                            :items="getYesNoDropdownItems" 
                            :label="$t('lang_GENERIC_AZ_CAPTION_GEMA_MEMBER')" 
                            :selection="true"
                            :alvqa="alvqa + '-dropdown-gema-member'"
                        />
                    </v-row>
                    <v-row>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="BgNineSplit"
                            :items="getYesNoDropdownItems" 
                            :label="$t('lang_GENERIC_AZ_CAPTION_BG9_FOR_GEMA_MEMBER')" 
                            :selection="true"
                            :alvqa="alvqa + '-dropdown-bg-nine-split'"
                        />
                    </v-row>
                    <v-row>
                        <BaseEditPageDropdown 
                            :context="context"
                            field="FreeAuthor"
                            :items="getYesNoDropdownItems" 
                            :label="$t('lang_GENERIC_AZ_CAPTION_FREE_AUTHOR')" 
                            :selection="true"
                            :alvqa="alvqa + '-dropdown-free-author'"
                        />
                    </v-row>
                </v-col>
            </v-row>
        </template>
    </BasePanel>
</template>

<script>
import BasePanel from '@/components/_base/BasePanel'
import BaseEditPageDropdown from '@/components/_editForm/BaseEditPageDropdown'
import BaseEditPageTextField from '@/components/_editForm/BaseEditPageTextField'
import BaseEditPageDatePicker from '@/components/_editForm/BaseEditPageDatePicker'
import HelpIcon from '@/components/_buttons/HelpIcon'
import {    DropdownGenericYesNo,
            DropdownAzMetadataLanguages,
            DropdownCurrencies,
            } from '@/assets/dropdowns/alvDropdowns.js'

export default {
    name: "MetadataPanel",
    components: {
        BasePanel,
        BaseEditPageDropdown,
        BaseEditPageTextField,
        BaseEditPageDatePicker,
        HelpIcon,
    },
    data() {
        return {
            context: "AzPersonsMetadata",
            alvqa: "az-persons-edit-form-panel-metadata",
        }
    },
    computed: {
        // Dropdowns
        languageItems() {
            return DropdownAzMetadataLanguages.map(lang => ({
                ...lang,
                text: this.$t(lang.abbr)
            }))
        },
        dropdownCurrency() { return DropdownCurrencies },
        yearItems() {
            let years = []
            let maxYear = new Date().getFullYear()
            for(let i = maxYear; i >= 1930; i--) {
                years.push(
                    {
                        text: i.toString(), 
                        value: i, 
                        alvqa: this.alvqa + '-dropdown-year-' + i
                    }
                )
            }
            return years
        },

        labelCompanyItems() { 
            let companyArr
            let companies = this.$store.getters.getLabelCompanies 

            if(companies !== undefined) {
                companyArr = JSON.parse(JSON.stringify(companies))
                companyArr.forEach(element => {
                    element['alvqa'] = this.alvqa + '-dropdown-label-company-' + element.alvId
                });
            }

            return companyArr
        },

        publishingCompanyItems() { 
            let companyArr
            let companies = this.$store.getters.getPublishingCompanies 

            if(companies !== undefined) {
                companyArr = JSON.parse(JSON.stringify(companies))
                companyArr.forEach(element => {
                    element['alvqa'] = this.alvqa + '-dropdown-publishing-company-' + element.alvId
                });
            }

            return companyArr
        },

        getYesNoDropdownItems() { return DropdownGenericYesNo },
    },
    created() {
        this.$store.commit('setLanguages')
    }
}
</script>